import DOMPurify from 'dompurify';

const URL_IN_TEXT_RE = /https?:\/\/[^\s<]+/gi;
const URL_TAIL_PUNCT_RE = /[.,;:!?)\]}]+$/;

const ALLOWED_TAGS = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
    'h2', 'h3', 'ul', 'ol', 'li', 'span', 'a', 'input', 'div', 'img',
];

const ALLOWED_ATTR = [
    'href', 'target', 'rel', 'class', 'style', 'type', 'checked', 'src', 'alt',
];

const ALLOWED_IMG_SRC_PREFIXES = [
    'https://team.tomi.la/storage/talent/tasks/',
    'http://team.tomi.la/storage/talent/tasks/',
    'https://team.tomi.la/storage/talent/cards/',
    'http://team.tomi.la/storage/talent/cards/',
    'https://team.tomi.la/storage/manufacturing/',
    'http://team.tomi.la/storage/manufacturing/',
];

/** Solo URLs de imágenes subidas para talento (tareas / comentarios de estrategia). */
export function isAllowedTaskDescriptionImageSrc(src) {
    const value = String(src || '').trim();
    if (!value || value.startsWith('data:')) return false;
    return ALLOWED_IMG_SRC_PREFIXES.some((prefix) => value.startsWith(prefix));
}

const PURIFY_CONFIG = {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
};

/** Quita signos de cierre típicos que no suelen formar parte del href. */
export function trimUrlHrefForLink(raw) {
    let href = String(raw);
    while (href.length > 'http://'.length && URL_TAIL_PUNCT_RE.test(href)) {
        const next = href.replace(URL_TAIL_PUNCT_RE, '');
        if (next === href) break;
        href = next;
    }
    return href;
}

/** Sanitiza HTML de descripción de tarea con DOMPurify. */
export function sanitizeTaskDescriptionHtml(html) {
    if (!html || typeof html !== 'string') return '';
    const clean = DOMPurify.sanitize(html, PURIFY_CONFIG);
    if (!clean) return '';

    const doc = new DOMParser().parseFromString(clean, 'text/html');
    if (!doc.body) return clean;

    const walk = (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const tag = node.tagName.toLowerCase();

        if (tag === 'a') {
            const href = (node.getAttribute('href') || '').trim();
            if (!/^https?:\/\/.+/i.test(href)) {
                const span = doc.createElement('span');
                while (node.firstChild) span.appendChild(node.firstChild);
                node.parentNode.replaceChild(span, node);
                walk(span);
                return;
            }
            node.setAttribute('href', href);
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
            if (!node.classList.contains('task-description-link')) {
                node.classList.add('task-description-link');
            }
            [...node.attributes].forEach((attr) => {
                if (!['href', 'target', 'rel', 'class'].includes(attr.name)) {
                    node.removeAttribute(attr.name);
                }
            });
            return;
        }

        if (tag === 'img') {
            const src = (node.getAttribute('src') || '').trim();
            if (!isAllowedTaskDescriptionImageSrc(src)) {
                node.parentNode?.removeChild(node);
                return;
            }
            node.setAttribute('src', src);
            const alt = (node.getAttribute('alt') || '').trim();
            if (alt) node.setAttribute('alt', alt);
            else node.removeAttribute('alt');
            [...node.attributes].forEach((attr) => {
                if (!['src', 'alt'].includes(attr.name)) {
                    node.removeAttribute(attr.name);
                }
            });
            return;
        }

        if (tag === 'input') {
            const type = (node.getAttribute('type') || '').toLowerCase();
            if (type !== 'checkbox') {
                const span = doc.createElement('span');
                while (node.firstChild) span.appendChild(node.firstChild);
                node.parentNode.replaceChild(span, node);
                walk(span);
                return;
            }
            node.setAttribute('type', 'checkbox');
            node.removeAttribute('id');
            node.removeAttribute('name');
            if (!node.hasAttribute('checked')) node.removeAttribute('checked');
            return;
        }

        if (tag === 'span' && node.getAttribute('style')) {
            const style = node.getAttribute('style');
            const colorMatch = /color:\s*([^;]+)/i.exec(style);
            if (!colorMatch) node.removeAttribute('style');
            else node.setAttribute('style', `color:${colorMatch[1].trim()}`);
        }

        if (tag === 'div' && !node.classList.contains('task-desc-checklist')) {
            const p = doc.createElement('p');
            while (node.firstChild) p.appendChild(node.firstChild);
            node.parentNode.replaceChild(p, node);
            walk(p);
            return;
        }

        const children = Array.from(node.childNodes);
        for (const child of children) walk(child);
    };

    Array.from(doc.body.childNodes).forEach(walk);
    return doc.body.innerHTML;
}

/** Convierte http(s)://… en nodos <a> dentro del HTML (solo vista). */
export function linkifyUrlsInDescriptionHtml(html) {
    if (!html || typeof html !== 'string') return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.body) return html;

    const linkifyTextNode = (textNode) => {
        const parent = textNode.parentElement;
        if (parent && parent.tagName) {
            const parentTag = parent.tagName.toLowerCase();
            if (parentTag === 'a' || parentTag === 'img') return;
        }
        const text = textNode.textContent || '';
        const matches = [...text.matchAll(URL_IN_TEXT_RE)];
        if (matches.length === 0) return;

        const frag = doc.createDocumentFragment();
        let last = 0;
        for (const m of matches) {
            const mi = m.index;
            if (mi > last) frag.appendChild(doc.createTextNode(text.slice(last, mi)));
            const raw = m[0];
            const href = trimUrlHrefForLink(raw);
            if (/^https?:\/\/.+/i.test(href)) {
                const a = doc.createElement('a');
                a.setAttribute('href', href);
                a.setAttribute('target', '_blank');
                a.setAttribute('rel', 'noopener noreferrer');
                a.setAttribute('class', 'task-description-link');
                a.textContent = href;
                frag.appendChild(a);
                const after = raw.slice(href.length);
                if (after) frag.appendChild(doc.createTextNode(after));
            } else {
                frag.appendChild(doc.createTextNode(raw));
            }
            last = mi + raw.length;
        }
        if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)));
        if (textNode.parentNode) textNode.parentNode.replaceChild(frag, textNode);
    };

    const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            linkifyTextNode(node);
            return;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            const children = Array.from(node.childNodes);
            for (const child of children) walk(child);
        }
    };

    Array.from(doc.body.childNodes).forEach(walk);
    return doc.body.innerHTML;
}

/** HTML listo para mostrar en vista (sanitize → linkify → sanitize). */
export function getTaskDescriptionHtmlForDisplay(html) {
    const sanitized = sanitizeTaskDescriptionHtml(html);
    return sanitizeTaskDescriptionHtml(linkifyUrlsInDescriptionHtml(sanitized));
}

/** Sincroniza atributo checked en checkboxes del contenedor DOM. */
export function syncCheckboxAttributesToHtml(container) {
    if (!container) return;
    const checkboxes = container.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((cb) => {
        if (cb.checked) cb.setAttribute('checked', '');
        else cb.removeAttribute('checked');
    });
}

/** Quita clases/atributos de Quill que no se necesitan al guardar. */
function stripQuillArtifacts(html) {
    if (!html || typeof html !== 'string') return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.body) return html;

    doc.body.querySelectorAll('[class]').forEach((el) => {
        const cls = el.getAttribute('class') || '';
        const kept = cls
            .split(/\s+/)
            .filter((c) => c && c === 'task-desc-checklist')
            .join(' ');
        if (kept) el.setAttribute('class', kept);
        else el.removeAttribute('class');
    });

    doc.body.querySelectorAll('[contenteditable]').forEach((el) => {
        el.removeAttribute('contenteditable');
    });

    return doc.body.innerHTML;
}

/** True si el HTML no aporta texto ni imágenes (p. ej. `<p><br></p>`). */
export function isEffectivelyEmptyDescription(html) {
    if (!html || typeof html !== 'string') return true;
    if (/<img\b/i.test(html)) return false;
    const text = html
        .replace(/<br\s*\/?>/gi, '')
        .replace(/<p>\s*<\/p>/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    return text === '';
}

/** HTML listo para persistir (Quill o vista → sanitize + checkboxes). */
export function prepareTaskDescriptionHtmlForSave(html) {
    if (!html || typeof html !== 'string') return '';
    if (isEffectivelyEmptyDescription(html)) return '';
    const stripped = stripQuillArtifacts(html.trim());
    const doc = new DOMParser().parseFromString(stripped, 'text/html');
    if (doc.body) syncCheckboxAttributesToHtml(doc.body);
    const withCheckboxes = doc.body ? doc.body.innerHTML : stripped;
    return sanitizeTaskDescriptionHtml(withCheckboxes);
}

export function escapeHtmlForDescription(text) {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
